// @ts-nocheck
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

interface Activity {
  id?: string;
  lead_id: string;
  activity_type: string;
  subject?: string;
  description?: string;
  scheduled_date?: string;
  completed_date?: string;
  status: string;
}

interface ActivityFormProps {
  open: boolean;
  onClose: () => void;
  leadId: string;
  activity?: Activity | null;
  onSave: (activity: Activity) => void;
}

const ActivityForm: React.FC<ActivityFormProps> = ({ 
  open, 
  onClose, 
  leadId, 
  activity, 
  onSave 
}) => {
  const { profile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    activity?.scheduled_date ? new Date(activity.scheduled_date) : undefined
  );
  const [selectedTime, setSelectedTime] = useState(
    activity?.scheduled_date ? format(new Date(activity.scheduled_date), 'HH:mm') : '09:00'
  );
  
  const [formData, setFormData] = useState<Activity>({
    lead_id: leadId,
    activity_type: 'call',
    subject: '',
    description: '',
    status: 'pending',
    ...activity
  });

  const activityTypes = [
    { value: 'call', label: 'Phone Call' },
    { value: 'email', label: 'Email' },
    { value: 'meeting', label: 'Meeting' },
    { value: 'demo', label: 'Demo' },
    { value: 'proposal', label: 'Proposal' },
    { value: 'note', label: 'Note' }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.activity_type) {
      toast.error('Activity type is required');
      return;
    }

    setLoading(true);
    try {
      const scheduledDateTime = selectedDate && selectedTime 
        ? new Date(`${format(selectedDate, 'yyyy-MM-dd')}T${selectedTime}:00`)
        : null;

      const activityData = {
        ...formData,
        sales_rep_id: profile?.id,
        lead_id: leadId,
        scheduled_date: scheduledDateTime?.toISOString() || null,
        completed_date: formData.status === 'completed' ? new Date().toISOString() : null
      };

      if (activity?.id) {
        // Update existing activity
        const { data, error } = await supabase
          .from('sales_activities')
          .update(activityData)
          .eq('id', activity.id)
          .select()
          .single();

        if (error) throw error;
        onSave(data);
        toast.success('Activity updated successfully');
      } else {
        // Create new activity
        const { data, error } = await supabase
          .from('sales_activities')
          .insert(activityData)
          .select()
          .single();

        if (error) throw error;
        onSave(data);
        toast.success('Activity created successfully');
      }

      onClose();
    } catch (error) {
      console.error('Error saving activity:', error);
      toast.error('Failed to save activity');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {activity?.id ? 'Edit Activity' : 'Log Activity'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="type">Activity Type *</Label>
            <Select 
              value={formData.activity_type} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, activity_type: value }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {activityTypes.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="subject">Subject</Label>
            <Input
              id="subject"
              value={formData.subject || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
              placeholder="Quick description of the activity"
            />
          </div>

          <div>
            <Label htmlFor="status">Status</Label>
            <Select 
              value={formData.status} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formData.status === 'pending' && (
            <>
              <div>
                <Label>Scheduled Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {selectedDate ? format(selectedDate, 'PPP') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <Label htmlFor="time">Time</Label>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <Input
                    id="time"
                    type="time"
                    value={selectedTime}
                    onChange={(e) => setSelectedTime(e.target.value)}
                  />
                </div>
              </div>
            </>
          )}

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Detailed notes about this activity..."
              rows={3}
            />
          </div>

          <div className="flex gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : (activity?.id ? 'Update' : 'Save')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ActivityForm;